(() => {
  'use strict';
  if (window.YoLinkPredictiveSearch) return;
  const cleanup = new WeakMap();
  const initialize = (scope = document) => scope.querySelectorAll('[data-yl-predictive-search]').forEach(form => {
    if (form.dataset.predictiveReady) return;
    form.dataset.predictiveReady = 'true';
    const input = form.querySelector('[name="q"]');
    const panel = form.querySelector('[data-yl-predictive-results]');
    const status = form.querySelector('[data-yl-predictive-status]');
    const disclosure = form.closest('details');
    let timer, deadline, controller, generation = 0, active = -1, composing = false;
    const listeners = [];
    const listen = (target, type, handler, options) => {
      target.addEventListener(type, handler, options);
      listeners.push(() => target.removeEventListener(type, handler, options));
    };
    const options = () => [...panel.querySelectorAll('[role="option"]')];
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', 'false');
    const cancel = () => {
      clearTimeout(timer);
      clearTimeout(deadline);
      controller?.abort();
      timer = deadline = controller = undefined;
      generation += 1;
      panel.removeAttribute('aria-busy');
    };
    const close = () => {
      cancel();
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      input.removeAttribute('aria-controls');
      active = -1;
      status.textContent = '';
    };
    const select = index => {
      const items = options();
      active = index;
      items.forEach((item, i) => item.setAttribute('aria-selected', String(i === active)));
      if (items[active]) {
        input.setAttribute('aria-activedescendant', items[active].id);
        items[active].scrollIntoView({ block: 'nearest' });
      } else input.removeAttribute('aria-activedescendant');
    };
    const request = async (query, version) => {
      const pending = new AbortController();
      controller = pending;
      const timeout = setTimeout(() => pending.abort(), 7000);
      deadline = timeout;
      panel.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(form.dataset.suggestUrl, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', 'yolink-predictive-search');
        url.searchParams.set('resources[type]', 'product,page,article');
        // Fetch enough candidates to avoid filling every product slot with packs
        // of one model. The section caps the visible product/content groups.
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, { signal: pending.signal });
        if (!response.ok) throw new Error('Search unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const results = doc.querySelector('[data-yl-suggestions]');
        if (!results) throw new Error('Search unavailable');
        if (version !== generation || !disclosure.open || document.activeElement !== input) return;
        panel.replaceChildren(results);
        panel.hidden = false;
        input.setAttribute('aria-controls', 'yl-search-suggestions');
        input.setAttribute('aria-expanded', 'true');
        active = -1;
        const count = Number(results.dataset.resultCount);
        status.textContent = count ? `${count} suggestions. Use the up and down arrow keys to browse, or press Enter to search all results.` : 'No quick matches. Press Enter to search all results.';
      } catch {
        if (version === generation) {
          close();
          status.textContent = 'Suggestions unavailable. Press Enter or choose Search to see results.';
        }
      } finally {
        clearTimeout(timeout);
        if (controller === pending) controller = deadline = undefined;
        if (version === generation) panel.removeAttribute('aria-busy');
      }
    };
    const schedule = () => {
      close();
      const query = input.value.trim();
      if (composing || query.length < 2 || !disclosure.open) return;
      const version = generation;
      timer = setTimeout(() => request(query, version), 200);
    };
    listen(input, 'input', schedule);
    listen(input, 'focus', schedule);
    listen(input, 'compositionstart', () => { composing = true; close(); });
    listen(input, 'compositionend', () => { composing = false; schedule(); });
    listen(input, 'keydown', event => {
      if (composing || event.isComposing) return;
      if (event.key === 'Escape' && !panel.hidden) {
        event.preventDefault(); event.stopPropagation(); close();
      } else if (!panel.hidden && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        const length = options().length;
        if (!length) return;
        select(event.key === 'ArrowDown' ? (active + 1) % length : active <= 0 ? length - 1 : active - 1);
      } else if (event.key === 'Enter' && !panel.hidden && active >= 0) {
        event.preventDefault();
        const option = options()[active];
        // Also tolerate a cached response from the previous section markup.
        (option.querySelector('a') || option).click();
      } else if (event.key === 'Tab') close();
    });
    listen(form, 'submit', event => {
      if (composing) { event.preventDefault(); return; }
      if (!window.YoLinkUrlContext) { close(); return; }
      const query = input.value.trim();
      if (!query) { close(); return; }
      try {
        const destination = new URL(form.dataset.productSearchUrl, location.origin);
        if (destination.origin !== location.origin || !/(?:^|\/)collections\/all\/?$/.test(destination.pathname)) throw Error('Unsafe catalog URL');
        destination.search = '';
        destination.searchParams.set('q', query);
        const current = new URL(location.href);
        window.YoLinkUrlContext.preserve(destination, current);
        event.preventDefault();
        // Claim only a validated catalog route before later form/app bubble listeners run.
        event.stopImmediatePropagation();
        close();
        location.assign(destination.href);
      } catch { close(); } // Native /search form remains the no-JS/invalid-route fallback.
    }, true);
    listen(form, 'focusout', event => { if (!form.contains(event.relatedTarget)) close(); });
    listen(disclosure, 'toggle', () => { if (!disclosure.open) close(); });
    cleanup.set(form, () => {
      close();
      listeners.forEach(remove => remove());
      delete form.dataset.predictiveReady;
      cleanup.delete(form);
    });
  });
  window.YoLinkPredictiveSearch = { initialize };
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-yl-predictive-search]').forEach(form => cleanup.get(form)?.());
  });
})();
